/** @format */
import { ChangeDetectionStrategy, Component, inject, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { FudisDialogService, FudisSelectOption } from '@funidata/ngx-fudis';
import { StudyPeriod, StudyPeriodLocator, StudyTerm } from 'common-typescript/types';
import moment from 'moment';
import { EMPTY, map, Observable } from 'rxjs';
import { LocaleService, mono, requestQueueIgnoreConcurrent } from 'sis-common';
import {
    AlertsService,
    AppErrorHandler,
    FudisFormValidatorsService,
    handleSubmission,
    LocalDateRangePipe,
    SisFormBuilder,
} from 'sis-components';
import { SelectableStudiesService } from '../select-course-units-modal/selectable-studies.service';
import { SelectableStudy } from '../select-course-units-modal/types';
import { TimingActionService } from '../service/timing-action.service';
import { CurrentAndUpcomingStudyTermsService } from '../timeline/current-and-upcoming-study-terms.service';

export function timeCourseUnitModalOpener(): (viewContainerRef: ViewContainerRef) => MatDialogRef<TimeCourseUnitModalComponent, null> {
    const fudisDialogService = inject(FudisDialogService);
    return viewContainerRef =>
        fudisDialogService.open<TimeCourseUnitModalComponent, null, null>(TimeCourseUnitModalComponent, { viewContainerRef });
}

interface TimeCourseUnitForm {
    courseUnit: FormControl<SelectableStudy | null>;
    studyPeriods: FormControl<FudisSelectOption<StudyPeriodLocator>[] | null>;
}

export type SelectablePeriod = FudisSelectOption<StudyPeriodLocator>;

export interface TermOptionGroup {
    id: string;
    label: string;
    options: SelectablePeriod[];
}

@Component({
    selector: 'app-time-course-unit-modal',
    standalone: false,
    templateUrl: './time-course-unit-modal.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [SelectableStudiesService],
})
export class TimeCourseUnitModalComponent {
    private readonly fb: SisFormBuilder = inject(SisFormBuilder);
    private readonly selectableStudiesService = inject(SelectableStudiesService);
    private readonly currentAndUpcomingStudyTermsService = inject(CurrentAndUpcomingStudyTermsService);
    private readonly localeService = inject(LocaleService);
    private readonly localDateRangePipe = inject(LocalDateRangePipe);
    private readonly timingActionService = inject(TimingActionService);
    private readonly alertsService = inject(AlertsService);
    private readonly appErrorHandler = inject(AppErrorHandler);
    private readonly fudisDialogService = inject(FudisDialogService);
    private readonly fudisValidators = inject(FudisFormValidatorsService);

    readonly untimedStudySelectOptions$ = this.selectableStudiesService.untimedStudySelectOptions$;

    readonly studyPeriodsByTerm$ = this.currentAndUpcomingStudyTermsService.studyTerms$.pipe(
        map(studyTerms => studyTerms.flatMap(studyTerm => this.getTermOptionGroup(studyTerm))),
    );

    save(): void {
        handleSubmission(this.form, this.saveQueue);
    }

    private readonly saveQueue = requestQueueIgnoreConcurrent<void, void>(
        () => mono(this.saveStudyForPeriods()),
        () => this.onSuccess(),
        this.appErrorHandler.defaultErrorHandler(),
        takeUntilDestroyed(),
    );

    protected readonly form = this.fb.group<TimeCourseUnitForm>({
        courseUnit: this.fb.control<SelectableStudy | null>(null, this.fudisValidators.required()),
        studyPeriods: this.fb.control<FudisSelectOption<StudyPeriodLocator>[] | null>([], this.fudisValidators.required()),
    });

    private getTermOptionGroup(studyTerm: StudyTerm): TermOptionGroup {
        return <TermOptionGroup>{
            id: studyTerm.valid.startDate,
            label: `${moment(studyTerm.valid.startDate).year()} - ${this.localeService.localize(studyTerm.name)}, ${this.localDateRangePipe.transform(studyTerm.valid)}`,
            options: studyTerm.studyPeriods.map(studyPeriod => this.getSelectablePeriod(studyPeriod)),
        };
    }

    private getSelectablePeriod(studyPeriod: StudyPeriod): SelectablePeriod {
        return <SelectablePeriod>{
            value: studyPeriod.locator,
            label: `${this.localeService.localize(studyPeriod.name)}, ${this.localDateRangePipe.transform(studyPeriod.valid)}`,
        };
    }

    private onSuccess(): void {
        this.alertsService.success('TIMELINE.TIME_COURSE_UNIT.SAVE_SUCCESS');
        this.fudisDialogService.close();
    }

    private saveStudyForPeriods(): Observable<void> {
        const study = this.form.controls.courseUnit.value?.value;
        const studyPeriods = new Set(this.form.controls.studyPeriods?.value.map(period => period.value));

        if (!study || !studyPeriods?.size) {
            return EMPTY;
        }
        if (study.type === 'CourseUnit') {
            return this.timingActionService.addNewCourseUnitTimingForPeriods(studyPeriods, study.value.id);
        }
        if (study.type === 'CustomStudyDraft') {
            return this.timingActionService.addNewCustomStudyDraftTimingForPeriods(studyPeriods, study.value.id);
        }
    }
}
